import { type LucideIcon } from "lucide-react";
import { type ReactNode } from "react";

import { cn } from "@/lib/utils";
import { slateCardClass } from "@/lib/card-styles";
import { typeMetaClass, typeNumericClass } from "@/lib/typography";

interface StatStripPrimary {
    label: string;
    value: ReactNode;
    icon?: LucideIcon;
    /** Short clarifier under the number, e.g. "Ready now". */
    hint?: ReactNode;
    /** Accent for the icon chip. Keep it to the four tier/action accents. */
    iconClassName?: string;
}

interface StatStripItem {
    label: string;
    value: ReactNode;
    hint?: ReactNode;
}

interface StatStripProps {
    /**
     * The one number that matters on this page. Rendered larger and given
     * its own column so the eye lands on it first.
     */
    primary: StatStripPrimary;
    /**
     * Supporting counts. Keep to three or four — a strip is not a grid, and
     * anything longer should become its own section or a tab.
     */
    secondary?: StatStripItem[];
    /**
     * Rich trailing content (a sparkline, a progress bar). Rendered inside the
     * same surface below the row, so detail never costs another card.
     */
    footer?: ReactNode;
    className?: string;
}

/**
 * A single row of related figures, in one surface.
 *
 * This replaces the four-to-five-card "stat wall" that previously headed most
 * pages. Those walls cost a full viewport before any actual content appeared,
 * and every card looked equally urgent, so nothing looked important.
 *
 * The hierarchy is the point: one primary figure, then quiet supporting
 * counts separated by hairlines. If a page genuinely has five things worth
 * showing, the fix is a tab or a section — not a sixth cell here.
 */
const StatStrip = ({ primary, secondary = [], footer, className }: StatStripProps) => {
    const PrimaryIcon = primary.icon;

    return (
        <div className={cn(slateCardClass, "flex flex-col sm:flex-row sm:flex-wrap sm:items-stretch", className)}>
            <div
                className={cn(
                    "flex min-w-0 flex-1 items-center gap-3 p-4 sm:p-5",
                    secondary.length > 0 &&
                        "border-b border-stone-200/70 sm:border-b-0 dark:border-stone-800"
                )}
            >
                {PrimaryIcon && (
                    <span
                        className={cn(
                            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                            primary.iconClassName ?? "bg-primary/10 text-primary"
                        )}
                    >
                        <PrimaryIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                )}
                <div className="min-w-0">
                    <p className={typeMetaClass}>{primary.label}</p>
                    <p className={cn(typeNumericClass, "mt-0.5 text-2xl font-bold leading-none sm:text-3xl")}>
                        {primary.value}
                    </p>
                    {primary.hint && (
                        <p className="mt-1 truncate text-xs text-stone-500 dark:text-stone-400">
                            {primary.hint}
                        </p>
                    )}
                </div>
            </div>

            {secondary.length > 0 && (
                <div className="flex flex-1 flex-col sm:flex-row sm:flex-wrap sm:items-stretch">
                    {secondary.map((item, i) => (
                        <div
                            key={item.label}
                            className={cn(
                                // Below `sm` each cell takes the full width. Sharing a
                                // 390px screen three ways left ~110px per cell, which
                                // truncated the uppercase labels to "MY GR…" / "MEMB…".
                                "flex min-w-0 flex-1 basis-full flex-col justify-center px-4 py-3 sm:basis-0 sm:px-5",
                                i > 0 && "border-t sm:border-l sm:border-t-0 border-stone-200/70 dark:border-stone-800"
                            )}
                        >
                            {/* Wraps rather than truncates: at 768 the three
                                labels clipped to "ACTIVE LE…" and "YOUR PERSONAL
                                TOP…", which names nothing. */}
                            <p className={cn(typeMetaClass, "text-balance")}>{item.label}</p>
                            <p className={cn(typeNumericClass, "mt-1 text-lg font-semibold leading-none")}>
                                {item.value}
                            </p>
                            {item.hint && (
                                <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
                                    {item.hint}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {footer && (
                <div className="w-full border-t border-stone-200/70 px-4 py-3 sm:px-5 dark:border-stone-800">
                    {footer}
                </div>
            )}
        </div>
    );
};

/**
 * Loading placeholder. Mirrors the strip's height so nothing shifts.
 *
 * `hasFooter` reserves the `footer` strip's row. Opt-in rather than always-on:
 * `StatStrip`'s footer is optional and only My Vocabulary's `StudyStats` passes
 * one (enrichment progress bar + 7-day chart). Without the flag that page
 * resolved from a 94px skeleton into a 182px strip, so the learning path and
 * the deck below jumped ~90px on every load. Every other caller passes no
 * footer and is unaffected by the default.
 *
 * `secondaryCount` mirrors how many supporting cells the real strip passes.
 * This was hardcoded to three, which matched **no** caller: Leaderboard passes
 * three, six of the other seven pass one or two. Below `sm` each cell is
 * `basis-full`, so the cells stack and one extra cell is a whole extra ~72px
 * row rather than a fraction of a column — measured on My Vocabulary, whose
 * `StudyStats` passes two, the skeleton resolved 386px into a 314px strip and
 * shoved the learning path and the whole deck up by 72px on every load.
 */
const StatStripSkeleton = ({
    className,
    hasFooter = false,
    secondaryCount = 2,
}: {
    className?: string;
    hasFooter?: boolean;
    /** Must match the real strip's `secondary` length. Defaults to 2, the
     *  count six of the seven callers use. */
    secondaryCount?: number;
}) => (
    <div
        role="status"
        aria-busy="true"
        className={cn(
            slateCardClass,
            "flex flex-col sm:flex-row sm:items-stretch",
            // `StatStrip` sets `sm:flex-wrap`, and the footer needs it: without
            // wrapping, `w-full` on a flex item does not push it onto its own
            // line and the footer renders as a third column beside the primary
            // figure instead of a full-width row underneath. Only added when a
            // footer is actually requested, so the other callers keep the exact
            // two-child row they had before.
            hasFooter && "sm:flex-wrap",
            className
        )}
    >
        <span className="sr-only">Loading statistics…</span>
        <div className="flex flex-1 animate-pulse items-center gap-3 border-b border-stone-200/70 p-4 sm:border-b-0 sm:p-5 dark:border-stone-800">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-stone-200/70 dark:bg-stone-800" />
            <div className="space-y-2">
                <div className="h-2.5 w-20 rounded-full bg-stone-200/70 dark:bg-stone-800" />
                <div className="h-6 w-12 rounded bg-stone-200/70 dark:bg-stone-800" />
                {/* Every StatStrip caller passes a `hint`, and it is taller than
                    the 40px icon above it — so the cell's height is set by the
                    label / value / hint stack, not the icon. Omitting this line
                    left the skeleton ~37px short on all seven callers. */}
                <div className="h-2.5 w-28 rounded-full bg-stone-100 dark:bg-stone-800/70" />
            </div>
        </div>
        <div className="flex flex-1 animate-pulse flex-col sm:flex-row sm:items-stretch">
            {[...Array(secondaryCount)].map((_, i) => (
                <div
                    key={i}
                    className={cn(
                        "flex flex-1 basis-full flex-col justify-center gap-2 px-4 py-3 sm:basis-0 sm:px-5",
                        i > 0 && "border-t sm:border-l sm:border-t-0 border-stone-200/70 dark:border-stone-800"
                    )}
                >
                    <div className="h-2.5 w-16 rounded-full bg-stone-100 dark:bg-stone-800/70" />
                    <div className="h-4 w-8 rounded bg-stone-200/70 dark:bg-stone-800" />
                    {/* Secondary `hint` — present on every caller's strip. */}
                    <div className="h-2.5 w-12 rounded-full bg-stone-100 dark:bg-stone-800/70" />
                </div>
            ))}
        </div>
        {hasFooter && (
            <div className="flex w-full animate-pulse flex-wrap items-center gap-x-6 gap-y-3 border-t border-stone-200/70 px-4 py-3 sm:px-5 dark:border-stone-800">
                <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="h-2.5 w-28 rounded-full bg-stone-200/70 dark:bg-stone-800" />
                        <div className="h-2.5 w-16 rounded-full bg-stone-100 dark:bg-stone-800/70" />
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-stone-100 dark:bg-stone-800/60" />
                </div>
                <div className="shrink-0 space-y-1.5">
                    <div className="h-2.5 w-20 rounded-full bg-stone-200/70 dark:bg-stone-800" />
                    <div className="flex h-5 items-end gap-0.5">
                        {[8, 14, 11, 20, 6, 17, 10].map((h, i) => (
                            <div
                                key={i}
                                className="w-1.5 rounded-sm bg-stone-200/70 dark:bg-stone-800"
                                style={{ height: h }}
                            />
                        ))}
                    </div>
                </div>
            </div>
        )}
    </div>
);

export { StatStrip, StatStripSkeleton };
export type { StatStripProps, StatStripPrimary, StatStripItem };
